import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Observable, of, Subject, Subscription, timer } from 'rxjs';
import { catchError, debounce, map, switchMap, tap } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { TramosDistanciaService } from '../../services/tramos-distancia.service';
import {
  erroresDe,
  formatoNumero,
  formatoPct,
  mensajeSinPermiso,
  mostrarPaginacion,
  paginaValida,
  statusDe,
  TEXTO_SOLO_LECTURA,
  totalPaginas,
} from './franjas-editor.rules';
import * as D from './tramos-distancia.rules';

export interface TramosDistanciaEditorData {
  familia: D.FamiliaDistancia;
  /** Respaldo si el API no manda `puedeEditar`. */
  puedeEditar: boolean;
  /** Ruta desde la que se abrió (se marca primero). */
  rutaInicial?: D.RutaUsdt;
}

type ResultadoVista = { ok: true; r: D.TablaDistanciaResponse } | { ok: false; errores: string[] };

/** Texto del 409 de la tabla de distancia. */
export const TEXTO_CONFLICTO_DISTANCIA = 'Otro operador cambió la tabla de distancia. Recarga para ver los cambios.';

/**
 * Editor de la tabla de distancia de una familia (VES, CLP→USDT o USDT→CLP).
 * Filas MIN / MAX / AJUSTE con su tasa resultante por ruta, vista previa
 * oficial con debounce, motivo, guardado con versión (409 → recargar) e
 * historial paginado. Sin permiso, solo lectura.
 *
 * Mismo lenguaje visual que el editor de tramos: reusa las clases `fe__*`.
 * Se cierra con `true` si guardó algo, para que el bloque relea.
 */
@Component({
  selector: 'app-tramos-distancia-editor',
  templateUrl: './tramos-distancia-editor.component.html',
  styleUrls: ['./franjas-editor.component.sass', './tramos-distancia-editor.component.sass'],
})
export class TramosDistanciaEditorComponent implements OnInit, OnDestroy {
  static readonly DEBOUNCE_MS = 700;

  readonly motivoMax = D.MOTIVO_DISTANCIA_MAX;
  readonly ajusteMax = D.AJUSTE_DISTANCIA_MAX;
  readonly filasMax = D.FILAS_MAX;
  readonly textoSoloLectura = TEXTO_SOLO_LECTURA;

  familia: D.FamiliaDistancia;
  rutas: D.RutaUsdt[];

  cargando = false;
  errorCarga: string | null = null;
  tabla: D.TablaDistanciaResponse | null = null;
  private version: string | number | null = null;
  private inicial: D.FilaDistancia[] | null = null;
  filas: D.FilaDistancia[] = [];

  motivo = '';
  previsualizando = false;
  guardando = false;
  vista: D.TablaDistanciaResponse | null = null;
  vistaVieja = false;
  /**
   * Hay una vista previa en camino (esperando el debounce o la respuesta):
   * hasta que llegue no se ofrece guardar lo que todavía no validó el servidor.
   */
  vistaPendiente = false;
  errores: string[] = [];
  avisos: string[] = [];
  mensaje: string | null = null;
  private guardoAlgo = false;

  historialAbierto = false;
  historialPagina = 1;
  historialItems: D.HistorialDistancia[] = [];
  historialTotal = 0;
  historialLeido = false;
  cargandoHistorial = false;
  errorHistorial: string | null = null;
  private subHistorial?: Subscription;

  private cambios$ = new Subject<{ inmediato: boolean }>();
  private subVista: Subscription;

  constructor(
    private svc: TramosDistanciaService,
    private ref: MatDialogRef<TramosDistanciaEditorComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: TramosDistanciaEditorData,
  ) {
    this.familia = data.familia;
    const rutas = D.rutasDeFamilia(data.familia);
    // La ruta desde la que se abrió va primero.
    this.rutas = data.rutaInicial && rutas.includes(data.rutaInicial)
      ? [data.rutaInicial, ...rutas.filter((r) => r !== data.rutaInicial)]
      : rutas;
    this.subVista = this.cambios$
      .pipe(
        // Al escribir espera DEBOUNCE_MS sin cambios; «inmediato» (cargar, Actualizar) va ya.
        debounce((c) => (c.inmediato ? of(0) : timer(TramosDistanciaEditorComponent.DEBOUNCE_MS))),
        switchMap(() => this.pedirVista()),
      )
      .subscribe((res) => this.aplicarVista(res));
  }

  ngOnInit(): void {
    this.cargar();
  }

  ngOnDestroy(): void {
    this.subVista?.unsubscribe();
    this.subHistorial?.unsubscribe();
  }

  // ───────────────────────── Lectura ─────────────────────────

  get titulo(): string {
    return D.etiquetaFamilia(this.familia);
  }

  get esVes(): boolean {
    return this.familia === 'VES';
  }

  get puedeEditar(): boolean {
    return typeof this.tabla?.puedeEditar === 'boolean' ? this.tabla.puedeEditar : !!this.data.puedeEditar;
  }

  get soloLectura(): boolean {
    return !this.puedeEditar;
  }

  get editable(): boolean {
    return this.puedeEditar && !this.guardando;
  }

  get hayCambios(): boolean {
    return D.hayCambiosFilas(this.filas, this.inicial);
  }

  get erroresLocales(): string[] {
    return D.erroresFilas(this.filas);
  }

  get avisosLocales(): string[] {
    return D.avisosFilas(this.filas);
  }

  get motivoValido(): boolean {
    return D.motivoDistanciaValido(this.motivo);
  }

  get puedeAgregar(): boolean {
    return this.filas.length < D.FILAS_MAX;
  }

  get puedeGuardar(): boolean {
    return (
      !!this.tabla &&
      this.puedeEditar &&
      this.hayCambios &&
      this.motivoValido &&
      !this.erroresLocales.length &&
      !this.vistaConErrores &&
      !this.guardando &&
      !this.previsualizando &&
      !this.vistaPendiente
    );
  }

  get motivoNoGuardar(): string {
    if (!this.puedeEditar) return 'Tu usuario no tiene permisos para modificar las tasas.';
    if (!this.hayCambios) return 'No hay cambios que guardar.';
    if (this.erroresLocales.length) return 'Corrige los campos marcados en rojo.';
    if (this.vistaConErrores) return 'Corrige los errores que marcó la vista previa.';
    if (!this.motivoValido) return 'Escribe el motivo del cambio (5 a 255 caracteres).';
    if (this.previsualizando || this.vistaPendiente) return 'Esperando la vista previa…';
    return '';
  }

  /**
   * La vista previa al día dice `ok: false` (errores del servidor, p. ej. un
   * ajuste que cruza la referencia con TRAMOS_NO_CRUZAR_REFERENCIA = '0'):
   * el PUT respondería 400, así que no se ofrece guardar.
   */
  get vistaConErrores(): boolean {
    return this.vistaAlDia && (this.vista?.ok === false || !!this.vista?.errores?.length);
  }

  /** La vista previa corresponde a lo que está en pantalla. */
  get vistaAlDia(): boolean {
    return !!this.vista && !this.vistaVieja && !this.previsualizando;
  }

  /** Margen, fila y puntas por ruta: de la vista previa si está al día, si no de lo leído. */
  activaDe(ruta: D.RutaUsdt): D.ActivaRuta | null {
    const fuente = this.vistaAlDia ? this.vista : this.tabla;
    return D.normalizarActiva(fuente).find((a) => a.ruta === ruta)
      ?? D.normalizarActiva(this.tabla).find((a) => a.ruta === ruta)
      ?? null;
  }

  /** Fila donde cae la ruta con las filas en pantalla (el margen no depende de la tabla). */
  filaActiva(ruta: D.RutaUsdt): number {
    return D.indiceFilaActiva(this.filas, this.activaDe(ruta)?.margenPct);
  }

  /** Rutas cuya fila activa es la `i` (para marcarla). */
  rutasEnFila(i: number): D.RutaUsdt[] {
    return this.rutas.filter((r) => this.filaActiva(r) === i);
  }

  /** Tasa del tramo grande de la ruta si su margen cayera en la fila `i`. */
  resultante(ruta: D.RutaUsdt, i: number): number | null {
    const a = this.activaDe(ruta);
    if (!a) return null;
    // En la fila activa, con la vista previa al día, la oficial del servidor.
    if (this.vistaAlDia && i === this.filaActiva(ruta) && a.grande != null) return a.grande;
    return D.tasaResultante(ruta, a.chica, this.filas[i]?.ajustePct);
  }

  /** La tasa de la fila es la oficial del servidor (no una estimación). */
  esOficial(ruta: D.RutaUsdt, i: number): boolean {
    return this.vistaAlDia && i === this.filaActiva(ruta) && this.activaDe(ruta)?.grande != null;
  }

  rotulos(ruta: D.RutaUsdt): D.RotulosDistancia {
    return D.rotulosDistancia(ruta);
  }

  etiquetaRuta(ruta: D.RutaUsdt): string {
    return D.etiquetaRuta(ruta);
  }

  colchon(ruta: D.RutaUsdt): number | null {
    const a = this.activaDe(ruta);
    const i = this.filaActiva(ruta);
    return D.colchonPct(a?.margenPct, i >= 0 ? this.filas[i]?.ajustePct : 0);
  }

  n(x: number | null | undefined, dec = 2): string {
    return formatoNumero(x, dec);
  }

  pct(x: number | null | undefined, dec = 2): string {
    return formatoPct(x, { dec });
  }

  /** «0,3» en es-CL; «—» sin dato. */
  lim(x: number | null | undefined): string {
    return x == null ? '—' : Number(x).toLocaleString('es-CL', { maximumFractionDigits: 4 });
  }

  resumenHistorial(h: D.HistorialDistancia): string {
    return D.resumenHistorialDistancia(h);
  }

  /** Las filas antes y después del cambio (el API guarda las dos). */
  antesDespues(h: D.HistorialDistancia): { antes: string; despues: string } {
    return D.antesDespuesHistorial(h);
  }

  trackIndice(i: number): number {
    return i;
  }

  // ───────────────────────── Carga ─────────────────────────

  cargar(conservarMensaje = false): void {
    this.cargando = true;
    this.errorCarga = null;
    const mensaje = this.mensaje;
    this.limpiarResultados();
    if (conservarMensaje) this.mensaje = mensaje;
    this.svc.obtenerTabla(this.familia).subscribe({
      next: (r) => {
        this.tabla = r;
        this.version = r?.version ?? null;
        this.inicial = D.clonarFilas(D.normalizarFilas(r?.filas));
        this.filas = D.clonarFilas(this.inicial);
        this.motivo = '';
        this.cargando = false;
        this.historialLeido = false;
        if (this.historialAbierto) this.irAPagina(1, true);
        this.cambios$.next({ inmediato: true });
      },
      error: (err) => {
        this.tabla = null;
        this.inicial = null;
        this.filas = [];
        const status = statusDe(err);
        this.errorCarga =
          status === 404 || status === 503
            ? 'La tabla de distancia todavía no está disponible en el servidor.'
            : erroresDe(err).join(' ');
        this.cargando = false;
      },
    });
  }

  // ───────────────────────── Edición ─────────────────────────

  private marcarEditado(): void {
    if (this.vista) this.vistaVieja = true;
    this.vistaPendiente = true;
    this.mensaje = null;
    this.cambios$.next({ inmediato: false });
  }

  cambiarMax(i: number, valor: unknown): void {
    this.filas = D.cambiarMax(this.filas, i, valor);
    this.marcarEditado();
  }

  cambiarAjuste(i: number, valor: unknown): void {
    this.filas = D.cambiarAjusteFila(this.filas, i, valor);
    this.marcarEditado();
  }

  agregar(): void {
    if (!this.editable || !this.puedeAgregar) return;
    this.filas = D.agregarFila(this.filas);
    this.marcarEditado();
  }

  quitar(i: number): void {
    if (!this.editable || this.filas.length <= D.FILAS_MIN) return;
    this.filas = D.quitarFila(this.filas, i);
    this.marcarEditado();
  }

  descartar(): void {
    if (this.inicial) this.filas = D.clonarFilas(this.inicial);
    this.limpiarResultados();
    this.cambios$.next({ inmediato: true });
  }

  /** Error de la celda MAX de la fila `i` (para marcarla en rojo). */
  errorMax(i: number): boolean {
    const f = this.filas[i];
    if (i === this.filas.length - 1) return false;
    return f?.maxPct == null || f.minPct == null || !(f.maxPct > f.minPct);
  }

  errorAjuste(i: number): boolean {
    const a = this.filas[i]?.ajustePct;
    return a == null || a < 0 || a > D.AJUSTE_DISTANCIA_MAX;
  }

  // ───────────────────────── Vista previa ─────────────────────────

  previsualizar(): void {
    this.cambios$.next({ inmediato: true });
  }

  private pedirVista(): Observable<ResultadoVista | null> {
    if (!this.tabla) return of(null);
    const locales = this.erroresLocales;
    if (locales.length) {
      this.previsualizando = false;
      return of({ ok: false, errores: locales } as ResultadoVista);
    }
    this.previsualizando = true;
    return this.svc.previsualizar(this.familia, this.filas).pipe(
      map((r) => ({ ok: true, r }) as ResultadoVista),
      catchError((err) =>
        of({
          ok: false,
          errores: statusDe(err) === 404 ? ['La vista previa no está disponible en el servidor.'] : erroresDe(err),
        } as ResultadoVista),
      ),
      tap(() => (this.previsualizando = false)),
    );
  }

  private aplicarVista(res: ResultadoVista | null): void {
    this.previsualizando = false;
    this.vistaPendiente = false;
    if (!res) return;
    if (res.ok) {
      this.vista = res.r;
      this.vistaVieja = false;
      this.errores = res.r?.errores || [];
      this.avisos = res.r?.avisos || [];
    } else {
      this.vista = null;
      this.vistaVieja = false;
      this.errores = res.errores;
      this.avisos = [];
    }
  }

  // ───────────────────────── Guardar ─────────────────────────

  async guardar(): Promise<void> {
    if (!this.puedeGuardar) return;
    const confirmacion = await Swal.fire({
      title: `¿Guardar la tabla de distancia ${this.titulo}?`,
      html: this.esVes
        ? '<p style="color:#92400e">Cambia el tramo grande de USDT → VES y de VES → USDT. La tasa chica (la publicada) no cambia.</p>'
        : '<p style="color:#92400e">Cambia el tramo grande de esta ruta. La tasa chica (la publicada) no cambia.</p>',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Guardar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1C593D',
      cancelButtonColor: '#6b7280',
      reverseButtons: true,
    });
    if (!confirmacion.isConfirmed) return;

    this.guardando = true;
    this.errores = [];
    this.avisos = [];
    this.mensaje = null;
    this.svc.guardar(this.familia, this.filas, this.motivo.trim(), this.version).subscribe({
      next: (r) => {
        this.guardando = false;
        this.guardoAlgo = true;
        // CLP→USDT se cobra desde el snapshot de la tasa base: el cambio no es instantáneo.
        this.mensaje = r?.rigeDesdeProximaBase
          ? 'Tabla de distancia guardada. Rige desde la próxima tasa base (2 a 4 min).'
          : 'Tabla de distancia guardada.';
        this.aviso('success', this.mensaje);
        this.cargar(true);
      },
      error: (err) => {
        this.guardando = false;
        const sinPermiso = mensajeSinPermiso(err);
        if (sinPermiso) {
          this.errores = [sinPermiso];
          Swal.fire({
            icon: 'warning',
            title: 'Acción no disponible',
            text: sinPermiso,
            confirmButtonText: 'Entendido',
            confirmButtonColor: '#f59e0b',
          });
          return;
        }
        if (statusDe(err) === 409) {
          this.errores = [TEXTO_CONFLICTO_DISTANCIA];
          this.avisoConflicto();
          return;
        }
        this.errores = erroresDe(err);
        this.aviso('error', 'No se pudo guardar la tabla. Revisa los errores.');
      },
    });
  }

  private async avisoConflicto(): Promise<void> {
    const r = await Swal.fire({
      icon: 'warning',
      title: 'Acción no disponible',
      text: TEXTO_CONFLICTO_DISTANCIA,
      showCancelButton: true,
      confirmButtonText: 'Recargar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#f59e0b',
      cancelButtonColor: '#6b7280',
      reverseButtons: true,
    });
    if (r.isConfirmed) this.cargar();
  }

  async cerrar(): Promise<void> {
    if (this.guardando) return;
    if (this.hayCambios && !this.soloLectura) {
      const r = await Swal.fire({
        icon: 'question',
        title: '¿Descartar los cambios?',
        text: 'La tabla no se guardó.',
        showCancelButton: true,
        confirmButtonText: 'Descartar',
        cancelButtonText: 'Seguir editando',
        confirmButtonColor: '#b91c1c',
        cancelButtonColor: '#6b7280',
        reverseButtons: true,
      });
      if (!r.isConfirmed) return;
    }
    this.ref.close(this.guardoAlgo);
  }

  private aviso(icon: 'success' | 'error', title: string): void {
    Swal.fire({
      toast: true,
      position: 'top-end',
      icon,
      title,
      showConfirmButton: false,
      timer: icon === 'success' ? 3500 : 5000,
      timerProgressBar: true,
    });
  }

  private limpiarResultados(): void {
    this.vista = null;
    this.vistaVieja = false;
    this.errores = [];
    this.avisos = [];
    this.mensaje = null;
  }

  // ───────────────────────── Historial ─────────────────────────

  get paginasHistorial(): number {
    return totalPaginas(this.historialTotal, D.HISTORIAL_DISTANCIA_TAMANO);
  }

  get hayPaginacion(): boolean {
    return mostrarPaginacion(this.historialTotal, D.HISTORIAL_DISTANCIA_TAMANO);
  }

  toggleHistorial(): void {
    this.historialAbierto = !this.historialAbierto;
    if (this.historialAbierto && !this.historialLeido) this.irAPagina(1, true);
  }

  irAPagina(pagina: number, forzar = false): void {
    const destino = forzar ? 1 : paginaValida(pagina, this.historialTotal, D.HISTORIAL_DISTANCIA_TAMANO);
    if (!forzar && (destino === this.historialPagina || this.cargandoHistorial)) return;
    this.cargandoHistorial = true;
    this.errorHistorial = null;
    this.subHistorial?.unsubscribe();
    this.subHistorial = this.svc.historial(this.familia, destino, D.HISTORIAL_DISTANCIA_TAMANO).subscribe({
      next: (r) => {
        this.cargandoHistorial = false;
        this.historialLeido = true;
        this.historialItems = r?.items || [];
        const total = Number(r?.total);
        this.historialTotal = Number.isFinite(total) && total >= 0 ? total : this.historialItems.length;
        this.historialPagina = Number(r?.pagina) || destino;
      },
      error: (err) => {
        this.cargandoHistorial = false;
        this.errorHistorial =
          statusDe(err) === 404 ? 'El historial todavía no está disponible.' : erroresDe(err).join(' ');
      },
    });
  }
}
